import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ChatStreamMode } from '../../constants/chatStreamMode';

/* На десктопе помещается больше чипов — раскладываем все доступные режимы в ряд,
   в «…» остаётся только «Задать вопрос». На мобильном — прежние 2 + «…». */
const INSIGHT_DESKTOP_MQ = '(min-width: 768px)';

function useInsightDesktopLayout() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(INSIGHT_DESKTOP_MQ).matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(INSIGHT_DESKTOP_MQ);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

export default function ChatInsightPanel({
  t,
  verseSessionContext,
  insightVerseExpanded,
  setInsightVerseExpanded,
  insightSurfaceAssistant,
  loading,
  normalizeAssistantHtml,
  insightActiveMode,
  setInsightActiveMode,
  followUpButtons,
  snapshot,
  runFollowUp,
  verseQuickExplainSubmit,
  verseQuickApplicationSubmit,
  setPresentationMode,
  inputRef,
  onRequestClose,
  hideCustomClose = false,
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const isDesktopLayout = useInsightDesktopLayout();
  const simpleBtn = followUpButtons.find((btn) => btn.mode === ChatStreamMode.Simple);
  const keyIdeaBtn = followUpButtons.find((btn) => btn.mode === ChatStreamMode.KeyIdea);
  const isExplainActive = insightActiveMode === 'explain' || insightActiveMode == null;

  const insightModeTitle =
    insightActiveMode === 'explain' || insightActiveMode == null
      ? t('verseExplain')
      : insightActiveMode === 'simple'
        ? simpleBtn?.label || t('chatFollowUpSimple')
        : insightActiveMode === 'keyIdea'
          ? keyIdeaBtn?.label || t('chatFollowUpKeyThought')
          : insightActiveMode === 'application'
            ? t('application')
            : t('chatActionLabel');

  const actionCatalog = {
    explain: {
      key: 'explain',
      label: t('verseExplain'),
      emoji: '\u{1F4D6}',
      disabled: loading,
      run: () => {
        setInsightActiveMode('explain');
        void verseQuickExplainSubmit();
      },
    },
    simple: {
      key: 'simple',
      label: simpleBtn?.label || t('chatFollowUpSimple'),
      emoji: simpleBtn?.emoji || '✨',
      disabled: loading || !simpleBtn,
      run: () => {
        if (!simpleBtn) return;
        setInsightActiveMode('simple');
        void runFollowUp(simpleBtn.mode);
      },
    },
    application: {
      key: 'application',
      label: t('application'),
      emoji: '\u{1F9ED}',
      disabled: loading,
      run: () => {
        setInsightActiveMode('application');
        void verseQuickApplicationSubmit();
      },
    },
    keyIdea: {
      key: 'keyIdea',
      label: keyIdeaBtn?.label || t('chatFollowUpKeyThought'),
      emoji: keyIdeaBtn?.emoji || '💡',
      disabled: loading || !keyIdeaBtn,
      run: () => {
        if (!keyIdeaBtn) return;
        setInsightActiveMode('keyIdea');
        void runFollowUp(keyIdeaBtn.mode);
      },
    },
  };

  // Очередь кнопок:
  // - в режиме "Разбор" чипы начинаются с Simple + Application (Разбор уже активен в заголовке);
  // - в остальных режимах первая кнопка = "Разбор", дальше — оставшиеся неактивные режимы.
  // На десктопе помещается больше чипов, поэтому раскрываем хвост из «…» в основной ряд.
  const basePriority = ['simple', 'application', 'keyIdea'];
  const activeKey = isExplainActive ? 'explain' : insightActiveMode;
  const orderedKeys = isExplainActive
    ? basePriority
    : ['explain', ...basePriority.filter((k) => k !== activeKey)];
  const primaryCount = isDesktopLayout ? orderedKeys.length : 2;
  const primaryKeys = orderedKeys.slice(0, primaryCount);
  const menuKeys = ['simple', 'application', 'keyIdea', 'explain'].filter(
    (k) => !primaryKeys.includes(k)
  );

  const primaryActions = primaryKeys.map((k) => actionCatalog[k]).filter(Boolean);
  const menuActions = menuKeys.map((k) => actionCatalog[k]).filter(Boolean);
  const toggleMoreMenu = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setMoreOpen((v) => !v);
  };

  useEffect(() => {
    if (!moreOpen) return;
    const onPointerDown = (e) => {
      if (!moreRef.current) return;
      if (moreRef.current.contains(e.target)) return;
      setMoreOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [moreOpen]);

  /** Синхронный commit → focus в том же user gesture (iOS / Telegram открывают клавиатуру). */
  const switchToChatComposerForQuestion = (closeMoreMenu = false) => {
    flushSync(() => {
      if (closeMoreMenu) setMoreOpen(false);
      setInsightActiveMode('question');
      setPresentationMode('chat');
    });
    const el = inputRef?.current;
    if (!el) return;
    el.focus();
    try {
      const n = el.value.length;
      el.setSelectionRange(n, n);
    } catch {
      /* setSelectionRange — не все типы input */
    }
  };

  return (
    <section className="chat-insight-panel" aria-label={`${insightModeTitle}. ${t('verseInsightDialogAria')}`}>
      <div className="chat-insight-top-bar">
        {!hideCustomClose && typeof onRequestClose === 'function' ? (
          <button
            type="button"
            className="chat-insight-top-bar-back"
            onClick={(e) => {
              e.stopPropagation();
              onRequestClose();
            }}
            aria-label={t('settingsBackAria')}
          >
            <span className="chat-insight-top-bar-back-chevron" aria-hidden="true">
              ‹
            </span>
            <span>{t('settingsBackAria')}</span>
          </button>
        ) : (
          <span className="chat-insight-top-bar-back-spacer" aria-hidden="true" />
        )}
        <h2 className="chat-insight-top-bar-title">{insightModeTitle}</h2>
        {typeof onRequestClose === 'function' ? (
          <button
            type="button"
            className="chat-insight-top-bar-close"
            onClick={(e) => {
              e.stopPropagation();
              onRequestClose();
            }}
            aria-label={t('close')}
            title={t('close')}
          >
            <span className="chat-insight-top-bar-close-icon" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="15" height="15" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </button>
        ) : null}
      </div>
      <div className="chat-page-verse-scroll chat-insight-verse-scroll">
        <div className="chat-page-session-context">
          <div className="chat-page-session-context-ref chat-insight-session-ref">
            <strong>{verseSessionContext.reference}</strong>
          </div>
          {verseSessionContext.verse ? (
            <button
              type="button"
              className={`chat-insight-verse-text-toggle ${insightVerseExpanded ? 'is-expanded' : ''}`}
              onClick={() => setInsightVerseExpanded((v) => !v)}
              aria-expanded={insightVerseExpanded}
              aria-label={
                insightVerseExpanded ? t('verseInsightVerseCollapseAria') : t('verseInsightVerseExpandAria')
              }
            >
              <div className="chat-page-session-context-verse">«{verseSessionContext.verse}»</div>
            </button>
          ) : null}
        </div>
      </div>
      <div className="chat-insight-body">
        {(() => {
          const a = insightSurfaceAssistant;
          const raw = String(a?.text || '');
          const hasText = raw.trim().length > 0;
          const busy = Boolean(a?.streaming) || (loading && a && !hasText);

          /* Как в ленте чата: пока нет объекта ответа — только индикатор загрузки. */
          if (!a) {
            if (!loading) return null;
            return (
              <div className="chat-insight-stream">
                <span className="chat-bubble-loading">{t('chatLoadingAnswer')}</span>
              </div>
            );
          }

          if (!hasText && busy) {
            return (
              <div className="chat-insight-stream">
                <span className="chat-bubble-loading">{t('chatLoadingAnswer')}</span>
                <span className="chat-stream-caret" aria-hidden="true">
                  ▍
                </span>
              </div>
            );
          }

          return (
            <div className="chat-insight-stream">
              <span
                className="chat-bubble-html"
                dangerouslySetInnerHTML={{ __html: normalizeAssistantHtml(raw) }}
              />
              {a.streaming ? <span className="chat-stream-caret" aria-hidden="true">▍</span> : null}
            </div>
          );
        })()}
      </div>
      <div className="chat-insight-actions-stack">
        <div
          className="chat-followup-row chat-page-verse-actions chat-insight-actions-row"
          role="group"
          aria-label={t('verseActions')}
        >
          {primaryActions.map((action) => (
            <button
              key={action.key}
              type="button"
              className="chat-followup-btn"
              disabled={action.disabled}
              onClick={() => action.run()}
            >
              <span className="chat-followup-btn-emoji" aria-hidden="true">
                {action.emoji}
              </span>
              <span>{action.label}</span>
            </button>
          ))}
          {isDesktopLayout ? (
            /* На десктопе все режимы — отдельные чипы, поэтому «…» избыточен:
               «Задать вопрос» выносим прямо в ряд, чтобы убрать лишний клик. */
            <button
              type="button"
              className="chat-followup-btn chat-insight-ask-chip"
              disabled={loading}
              onClick={() => switchToChatComposerForQuestion(false)}
            >
              <span className="chat-followup-btn-emoji" aria-hidden="true">
                {'\u{1F4AC}'}
              </span>
              <span>{t('verseInsightAskQuestion')}</span>
            </button>
          ) : (
            <div ref={moreRef} className={`chat-insight-more ${moreOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className="chat-followup-btn"
                disabled={loading}
                aria-expanded={moreOpen}
                onClick={toggleMoreMenu}
              >
                <span className="chat-insight-more-trigger" aria-hidden="true">
                  ⋯
                </span>
              </button>
              {moreOpen && (
                <div className="chat-insight-more-menu">
                  {menuActions.map((action) => (
                    <button
                      key={action.key}
                      type="button"
                      className="chat-insight-more-item"
                      disabled={action.disabled}
                      onClick={() => {
                        setMoreOpen(false);
                        action.run();
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="chat-insight-more-item"
                    disabled={loading}
                    onClick={() => switchToChatComposerForQuestion(true)}
                  >
                    {t('verseInsightAskQuestion')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
